import { jsxs, jsx } from 'react/jsx-runtime';
import { createContext, useContext, useState, useRef, useEffect, useCallback, Suspense } from 'react';
import { sendAdobeEvent } from '../../services/adobeService.js';
import { computeSyncedCart, getCart, changeArticleQuantity, addSavingsPlanArticle, deleteSavingsPlanArticle, addCoupon, removeCoupon } from '../../services/cartService.js';
import { setStaticCart, postEventGetCartIdSuccess, postEventGetCartSuccess, postEventGetCartFailure, registerEventsListeners, postEventAddToCartSuccess, postEventAddToCartFailure, setStaticSid, setStaticSetters } from '../../services/eventsService.js';
import { getSid } from '../../services/shopService.js';
import { getCartApiVersion } from '../../services/stateService.js';
import { useBrowserOnly } from '../../utils/browserUtils.js';
import { findArticleById, validateCartPayload } from '../../utils/cartUtils.js';
import PopoverWrapper from '../PopoverWrapper/PopoverWrapper.js';

const Context = createContext({});
const useCartContext = () => useContext(Context);
const useCartArticle = (articleId) => {
    const { cart } = useCartContext();
    if (articleId && cart) {
        return findArticleById(cart, articleId);
    }
    return undefined;
};
const CartProvider = ({ stage, children, cartVersion, }) => {
    const [sid, setSid] = useState();
    const [cart, setCart] = useState();
    const [actionInfo, setActionInfo] = useState();
    const [cartApiVersion, setCartApiVersion] = useState(cartVersion);
    const browserOnly = useBrowserOnly();
    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        const setAsyncCartApiVersion = async () => {
            const asyncCartApiVersion = cartVersion ?? (await getCartApiVersion(stage));
            setCartApiVersion(asyncCartApiVersion);
        };
        setAsyncCartApiVersion();
    }, [cartVersion, stage]);
    const setSidMountedSafe = (sid) => {
        mountedRef.current && setSid(sid);
    };
    const setCartMountedSafe = useCallback(async (newCart, isOriginCartEvents = false) => {
        if (isOriginCartEvents) {
            const syncedCart = await computeSyncedCart(cart, newCart, stage, sid);
            mountedRef.current && setCart(syncedCart);
        }
        else {
            mountedRef.current && setCart(newCart);
        }
    }, [cart, sid, stage]);
    const setActionInfoMountedSafe = (actionInfo) => {
        mountedRef.current && setActionInfo(actionInfo);
    };
    if (browserOnly) {
        setStaticSid(sid);
        cart && setStaticCart(cart, stage, true);
        setStaticSetters({
            setSid: setSidMountedSafe,
            setCart: setCartMountedSafe,
            setActionInfo: setActionInfoMountedSafe,
        });
    }
    const refreshSid = useCallback(async () => {
        const sid = await getSid(stage);
        setSidMountedSafe(sid);
        setStaticSid(sid);
        postEventGetCartIdSuccess(sid);
        return sid;
    }, [stage]);
    const refreshCart = useCallback(async () => {
        const scopeSid = sid ?? (await refreshSid());
        try {
            const response = await getCart({
                stage,
                sid: scopeSid,
                cartVersion: cartApiVersion,
            });
            await setCartMountedSafe(response, false);
            await setStaticCart(response, stage, true);
            await postEventGetCartSuccess({ cart: response, isOriginCartProvider: true, stage });
            return response;
        }
        catch (error) {
            console.error(error);
            postEventGetCartFailure(error);
            throw error;
        }
    }, [stage, sid, refreshSid, setCartMountedSafe, cartApiVersion]);
    useEffect(() => {
        if (!sid) {
            refreshCart();
        }
    }, [sid, refreshCart]);
    useEffect(() => {
        if (sid) {
            registerEventsListeners(stage);
        }
    }, [stage, sid]);
    const changeArticleQuantity$1 = useCallback(async (request) => {
        const { popover, ...baseRequest } = request;
        try {
            const error = validateCartPayload(request, cartApiVersion);
            if (error) {
                throw new Error(error);
            }
            const articleBeforeUpdate = findArticleById(cart, baseRequest.articleId);
            const response = await changeArticleQuantity({
                sid: sid,
                request: baseRequest,
                previousQuantity: articleBeforeUpdate?.quantity ?? 0,
                cartVersion: cartApiVersion,
                stage,
                popover,
            });
            const articleAfterUpdate = findArticleById(response.cart, baseRequest.articleId);
            await setCartMountedSafe(response.cart, false);
            if (popover) {
                setActionInfoMountedSafe({
                    quantity: articleAfterUpdate?.quantity ?? 0,
                    status: response.status,
                    popover,
                });
            }
            await setStaticCart(response.cart, stage, true);
            const requestWithStatus = {
                ...request,
                status: response.status,
                trigger: request.popover?.trigger,
            };
            delete requestWithStatus.popover;
            const articleForEvent = articleAfterUpdate ?? articleBeforeUpdate;
            if (articleForEvent) {
                const isCartApiV2 = cartApiVersion === 'v2';
                let isPersonalized = !!articleAfterUpdate?.articleBasedVoucher?.isPersonalized;
                let articleForEventPrice = articleForEvent?.price;
                if (isCartApiV2) {
                    isPersonalized = !!articleAfterUpdate?.lineItemDiscounts
                        ?.articleBasedDiscount?.isPersonalized;
                    articleForEventPrice = articleForEvent?.price?.value;
                }
                const quantityAdded = (articleAfterUpdate?.quantity ?? 0) - (articleBeforeUpdate?.quantity ?? 0);
                if (quantityAdded) {
                    sendAdobeEvent({
                        price: articleForEventPrice,
                        shopId: articleForEvent?.shopId,
                        psId: articleForEvent?.psId,
                        bestPriceArticle: articleForEvent?.bestPriceArticle,
                        sellerType: articleForEvent?.sellerType ?? '1P',
                    }, response.cart.cartId, quantityAdded, isPersonalized);
                }
            }
            await postEventAddToCartSuccess({
                cart: response.cart,
                stage,
                status: response.status,
                request: requestWithStatus,
                isOriginCartProvider: true,
            });
            return response;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            if (popover) {
                setActionInfoMountedSafe({
                    error,
                    quantity: request.quantity ?? 0,
                    popover,
                });
            }
            postEventAddToCartFailure(error, request);
            throw error;
        }
    }, [stage, cart, sid, cartApiVersion, setCartMountedSafe]);
    const handleCouponOrSavingsPlanResponse = useCallback(async (func) => {
        try {
            const response = await func();
            await setCartMountedSafe(response, false);
            await setStaticCart(response, stage, true);
            await postEventAddToCartSuccess({ cart: response, isOriginCartProvider: true, stage });
            return response;
        }
        catch (error) {
            console.error(error);
            postEventAddToCartFailure(error);
            throw error;
        }
    }, [stage, setCartMountedSafe]);
    const addSavingsPlanArticle$1 = useCallback(async (articleId) => {
        return handleCouponOrSavingsPlanResponse(() => addSavingsPlanArticle({
            stage,
            sid: sid,
            articleId,
            cartVersion: cartApiVersion,
        }));
    }, [stage, sid, handleCouponOrSavingsPlanResponse, cartApiVersion]);
    const deleteSavingsPlanArticle$1 = useCallback(async () => {
        return handleCouponOrSavingsPlanResponse(() => deleteSavingsPlanArticle({
            stage,
            sid: sid,
            cartVersion: cartApiVersion,
        }));
    }, [stage, sid, handleCouponOrSavingsPlanResponse, cartApiVersion]);
    const addCoupon$1 = useCallback(async (couponCode) => {
        return handleCouponOrSavingsPlanResponse(() => addCoupon({
            stage,
            sid: sid,
            couponCode,
            cartVersion: cartApiVersion,
        }));
    }, [stage, sid, handleCouponOrSavingsPlanResponse, cartApiVersion]);
    const removeCoupon$1 = useCallback(async (couponCode) => {
        return handleCouponOrSavingsPlanResponse(() => removeCoupon({
            stage,
            sid: sid,
            couponCode,
            cartVersion: cartApiVersion,
        }));
    }, [stage, sid, handleCouponOrSavingsPlanResponse, cartApiVersion]);
    const context = {
        ready: !!cart,
        sid,
        cart: cart,
        refreshCart,
        changeArticleQuantity: changeArticleQuantity$1,
        addSavingsPlanArticle: addSavingsPlanArticle$1,
        deleteSavingsPlanArticle: deleteSavingsPlanArticle$1,
        addCoupon: addCoupon$1,
        removeCoupon: removeCoupon$1,
        cartVersion: cartApiVersion,
    };
    return (jsxs(Context.Provider, { value: context, children: [browserOnly && actionInfo && (jsx(Suspense, { fallback: null, children: jsx(PopoverWrapper, { stage: stage, actionInfo: actionInfo }) })), children] }));
};

export { CartProvider, useCartArticle, useCartContext };
//# sourceMappingURL=CartProvider.js.map
